/**
 * The agent's own transcript.
 *
 * Newest first, because the question is almost always "what has it just done" or
 * "why did it stop". Commands are off by default: there are many, they are the least
 * interesting thing the agent does, and they bury the tool calls that decide the job.
 *
 * Tool calls are rendered from the display contract rather than dumped: what leads,
 * what reads as prose and what waits behind a disclosure is declared per tool in
 * `agent_trace_display.json`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { Link } from 'react-router-dom'
import { messageLink } from './board'
import { RUN_ZONE } from '@/lib/format'
import { Section } from '@/components/Section'
import {
  chipValue, displayFor, ERROR_DISPLAY, errorOf, fieldLabel, payloadOf, pick,
} from './contract'
import type { Trace, TraceEntry } from './types'
import { useBuilderMessages } from './BuilderMessage'

const TRACE_FILTERS: { id: string; label: string; kinds: string[] }[] = [
  { id: 'tool', label: 'Tools', kinds: ['tool'] },
  { id: 'message', label: 'Messages', kinds: ['message', 'prompt'] },
  { id: 'reasoning', label: 'Thinking', kinds: ['reasoning'] },
  { id: 'command', label: 'Commands', kinds: ['command', 'edit'] },
  { id: 'other', label: 'Other', kinds: ['output', 'error'] },
]
const KIND_ICON: Record<string, 'chat' | 'zap' | 'terminal' | 'file' | 'alert' | 'info'> = {
  message: 'chat', prompt: 'chat', reasoning: 'info', tool: 'zap',
  command: 'terminal', edit: 'file', error: 'alert', output: 'info',
}

const actionClock = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: RUN_ZONE,
})
const actionDate = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium', timeStyle: 'long', timeZone: RUN_ZONE,
})

function ActionTime({ entry }: { entry: TraceEntry }) {
  const timestamp = entry.started_at || entry.completed_at || entry.timestamp
  if (!timestamp || Number.isNaN(Date.parse(timestamp))) return null
  const label = entry.started_at ? 'Started' : entry.completed_at ? 'Completed' : 'Recorded'
  const details = [`${label} ${actionDate.format(new Date(timestamp))}`]
  if (entry.started_at && entry.completed_at && !Number.isNaN(Date.parse(entry.completed_at))) {
    details.push(`Completed ${actionDate.format(new Date(entry.completed_at))}`)
  }
  return <time className="trace-time" dateTime={timestamp} title={details.join(' · ')} aria-label={details.join(' · ')}>{actionClock.format(new Date(timestamp))}</time>
}

function Chips({ fields }: { fields: [string, unknown][] }) {
  if (!fields.length) return null
  return <div className="trace-fields">{fields.map(([path, value]) => (
    <span className="trace-field" key={path}>
      <span className="trace-field-name">{fieldLabel(path)}</span>
      <span className="trace-field-value">{chipValue(value)}</span>
    </span>
  ))}</div>
}

function Prose({ fields }: { fields: [string, unknown][] }) {
  return <>{fields.map(([path, value]) => (
    <p className="trace-prose" key={path}>{typeof value === 'string' ? value : JSON.stringify(value)}</p>
  ))}</>
}

function More({ label, value }: { label: string; value: unknown }) {
  if (value === undefined || value === null) return null
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  if (!text || text === '{}') return null
  return <details className="trace-output"><summary>{label}</summary><pre>{text}</pre></details>
}

/** One side of a call -- what was asked, or what came back -- ranked by the contract. */
function Payload({ label, value, inline, prose }: {
  label: string; value: unknown; inline?: string[]; prose?: string[]
}) {
  if (value === undefined || value === null) return null
  return <>
    <Chips fields={pick(value, inline).named} />
    <Prose fields={pick(value, prose).named} />
    <More label={label} value={pick(value, [...(inline ?? []), ...(prose ?? [])]).rest} />
  </>
}

const BOARD_TOOLS = new Set(['WriteBoard', 'ReplyMessage', 'SearchBoard', 'RecentBoard'])
const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
const string = (value: unknown) => typeof value === 'string' ? value : ''

function BoardResult({ tool, value }: { tool: string; value: unknown }) {
  const result = record(value)
  if (!result) return <More label="Board result" value={value} />
  if (tool === 'WriteBoard' || tool === 'ReplyMessage') {
    const id = string(result.id)
    return id ? <Link className="trace-board-link" to={messageLink(id)}><Icon name="chat" size={14} />Open message<Icon name="arrow" size={13} /></Link> : null
  }
  const items = Array.isArray(result.items) ? result.items : []
  return <div className="trace-board-results">
    <span className="muted small">{items.length} {items.length === 1 ? 'message' : 'messages'} returned</span>
    {items.map((item, index) => {
      const row = record(item)
      const thread = record(row?.thread)
      const matched = record(row?.match) ?? record(row?.reply) ?? thread
      const id = string(thread?.id)
      return id && <Link key={`${id}-${index}`} to={messageLink(id, matched?.kind === 'reply' ? string(matched.id) : undefined)} className="trace-board-result">
        <span>{string(matched?.content) || 'Open message'}</span><Icon name="arrow" size={13} />
      </Link>
    })}
  </div>
}

function BoardNotices({ value }: { value: unknown }) {
  const outer = record(value)
  const notices = Array.isArray(outer?.board_replies) ? outer.board_replies : []
  if (!notices.length) return null
  return <section className="trace-board-notices" aria-label="Board notifications">
    <div className="trace-board-notices-heading"><Icon name="chat" size={14} />
      <span>Board {notices.length === 1 ? 'update' : 'updates'}</span>
      <span className="muted">{notices.length} delivered to agent</span>
    </div>
    <ul>{notices.map((notice, index) => {
      const item = record(notice)
      const reply = record(item?.reply)
      const correction = record(item?.correction)
      const deleted = reply?.kind === 'deletion'
      const corrected = reply?.kind === 'correction' || (!reply && !!correction)
      const id = string(item?.thread_id)
      const content = deleted ? 'Do not pursue this thread further.' : string(reply?.content) || string(correction?.content)
      const latestCorrection = string(correction?.content)
      const author = string(reply?.author) || string(correction?.author)
      return <li key={`${id}-${index}`}>
        <div className="trace-board-notice-meta">
          <span>{deleted ? 'Thread deleted' : corrected ? 'Developer correction' : 'Board reply'}</span>
          {author && <span className="muted">{author}</span>}
        </div>
        {content && <p className="trace-board-notice-message">{content}</p>}
        {!deleted && latestCorrection && latestCorrection !== content && <div className="trace-board-notice-correction">
          <span className="muted">Latest developer correction</span>
          <p className="trace-board-notice-message">{latestCorrection}</p>
        </div>}
        {id && !deleted && <Link className="trace-board-notice-link" to={messageLink(id, reply?.kind === 'reply' ? string(reply.id) : undefined)}>
          View thread<Icon name="arrow" size={13} />
        </Link>}
      </li>
    })}</ul>
  </section>
}

function ToolItem({ entry }: { entry: TraceEntry }) {
  const display = displayFor(entry.title)
  const status = String(entry.meta.status ?? '')
  const failure = errorOf(entry.result)
  // A tool that refuses is not a broken tool: the contract's own tone still leads,
  // so NotifyDev stays red whether or not the call itself succeeded.
  const tone = display.tone ?? (failure || status === 'failed' ? 'error' : '')
  const outer = record(entry.result)
  const result = outer && 'result' in outer ? outer.result : payloadOf(entry.result)
  return <li className={`trace-item trace-tool${tone ? ` trace-tone-${tone}` : ''}`}>
    <span className="trace-mark"><Icon name={BOARD_TOOLS.has(entry.title) ? 'chat' : 'zap'} size={14} /></span>
    <div className="trace-body">
      <div className="trace-head">
        <span className="trace-title">{entry.title}</span>
        {display.label && <span className="trace-detail">{display.label}</span>}
        {status === 'failed' && <span className="chip err">failed</span>}
        {status === 'running' && <span className="chip">no result</span>}
        <ActionTime entry={entry} />
      </div>
      <Payload label="Other arguments" value={entry.arguments}
               inline={display.arguments?.inline} prose={display.arguments?.prose} />
      {failure
        ? <div className="trace-rejection">
            <Payload label="Rest of the rejection" value={failure}
                     inline={ERROR_DISPLAY.inline} prose={ERROR_DISPLAY.prose} />
          </div>
        : BOARD_TOOLS.has(entry.title)
          ? <BoardResult tool={entry.title} value={result} />
          : <Payload label="Rest of the result" value={result}
                     inline={display.result?.inline} prose={display.result?.prose} />}
      <BoardNotices value={entry.result} />
    </div>
  </li>
}

function PlainItem({ entry }: { entry: TraceEntry }) {
  const status = String(entry.meta.status ?? '')
  const detail = [entry.meta.cwd, entry.meta.exit_code != null ? `exit ${entry.meta.exit_code}` : '']
    .filter(Boolean).join(' · ')
  // Prose is read; output is scanned. Only the latter gets a monospace scroll box.
  const prose = ['reasoning', 'message', 'prompt', 'error'].includes(entry.kind)
  const usageLimit = entry.kind === 'error' && /(?:hit|reached|exceeded) (?:your |the )?usage limit/i.test(entry.body)
  return <li className={`trace-item trace-${entry.kind}${status ? ` trace-${status}` : ''}`}>
    <span className="trace-mark"><Icon name={KIND_ICON[entry.kind] ?? 'info'} size={14} /></span>
    <div className="trace-body">
      <div className="trace-head"><span className="trace-title">{usageLimit ? 'Usage limit reached' : entry.title}</span>
        {Boolean(entry.meta.delivery) && <span className="trace-detail">{String(entry.meta.delivery)}</span>}
        {detail && <span className="trace-detail">{detail}</span>}
        {status === 'failed' && <span className="chip err">failed</span>}
        <ActionTime entry={entry} />
      </div>
      {usageLimit ? <>
        <p className="trace-prose">The Codex account ran out of usage. Restore its allowance, then restart the build.</p>
        <More label="Provider details" value={entry.body} />
      </> : entry.body && (prose
        ? <p className="trace-prose">{entry.body}</p>
        : <details className="trace-output"><summary>{entry.body.split('\n')[0].slice(0, 120) || 'output'}</summary><pre>{entry.body}</pre></details>)}
    </div>
  </li>
}

export function AgentTrace({ taskId, jobId, live }: { taskId: string; jobId: string; live: boolean }) {
  const messages = useBuilderMessages(taskId, jobId, live)
  const [trace, setTrace] = useState<Trace>()
  const [error, setError] = useState('')
  const cursor = useRef<number | string | undefined>(undefined)
  const execution = useRef<string | undefined>(undefined)
  const [older, setOlder] = useState<TraceEntry[]>([])
  const [before, setBefore] = useState<string | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const conversation = useRef<string | undefined>(undefined)
  useEffect(() => { cursor.current = undefined; execution.current = undefined; conversation.current = undefined; setTrace(undefined); setOlder([]); setBefore(null) }, [taskId, jobId])
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(['reasoning', 'command']))
  const load = useCallback(() => {
    api.get<Trace>(`/eng/tasks/${taskId}/authoring/${jobId}/trace${cursor.current === undefined ? '' : `?after=${cursor.current}&execution_id=${encodeURIComponent(execution.current || '')}`}`)
      .then(value => {
        cursor.current = value.cursor; execution.current = value.execution_id
        if (!value.unchanged) {
          if (conversation.current !== value.conversation_id) { setOlder([]); setBefore(value.before ?? null) }
          conversation.current = value.conversation_id
          setTrace(previous => previous && previous.conversation_id === value.conversation_id
            ? { ...value, entries: Array.from(new Map([...previous.entries, ...value.entries].map(e => [String(e.meta.message_id || JSON.stringify(e)), e])).values()) }
            : value)
        }
        setError('')
      })
      .catch(e => setError(String(e)))
  }, [taskId, jobId])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!live) return
    const timer = window.setInterval(() => { if (!document.hidden) load() }, 3000)
    return () => clearInterval(timer)
  }, [live, load])

  const loadOlder = async () => {
    if (!before || loadingOlder) return
    const current = conversation.current
    setLoadingOlder(true)
    try {
      const value = await api.get<Trace>(`/eng/tasks/${taskId}/authoring/${jobId}/trace?before=${encodeURIComponent(before)}`)
      if (current !== conversation.current || value.conversation_id !== current) return
      setOlder(previous => [...previous, ...value.entries])
      setBefore(value.before ?? null)
      setError('')
    } catch (e) { setError(String(e)) }
    finally { setLoadingOlder(false) }
  }
  const allowed = new Set(TRACE_FILTERS.filter(f => !hidden.has(f.id)).flatMap(f => f.kinds))
  // The parser appends in log order; the reader wants the newest thing at the top.
  const messageIds = new Set(messages.entries.map(e => e.meta.message_id))
  const seen = new Set<string>()
  const history = [...(trace?.entries ?? []), ...older].filter(entry => {
    const identity = String(entry.meta.message_id || JSON.stringify(entry))
    if (seen.has(identity)) return false
    seen.add(identity)
    return true
  })
  const combined = [...history.filter(e => !messageIds.has(e.meta.message_id)), ...messages.entries]
  const entries = combined.filter(e => allowed.has(e.kind)).map((entry, index) => ({ entry, index }))
    .sort((a, b) => a.entry.timestamp && b.entry.timestamp
      ? Date.parse(b.entry.timestamp) - Date.parse(a.entry.timestamp) || b.index - a.index
      : b.index - a.index).map(({ entry }) => entry)
  return <Section title="Activity"
                  lead={<span title="Agent output is persisted by the authoring service and refreshed every three seconds.">Newest first · Zurich time · Live agent output</span>}
                  aside={<button className="btn sm" onClick={load}>Refresh</button>}>
    <div className="trace-filters" role="group" aria-label="Activity types">{TRACE_FILTERS.map(f => (
      <button type="button" key={f.id} aria-pressed={!hidden.has(f.id)} onClick={() => setHidden(prev => {
        const next = new Set(prev)
        if (next.has(f.id)) next.delete(f.id); else next.add(f.id)
        return next
      })}>{f.label}</button>
    ))}</div>
    {messages.composer}
    {!trace && !error && <p className="muted small">Loading activity…</p>}
    {error && !messages.entries.length && <p className="empty">No transcript yet. It appears once the supervisor has preserved the agent's log.</p>}
    {(trace || messages.entries.length > 0) && <>
      {trace?.partial && <p className="muted small">Some saved conversation history is unavailable. Live activity is still shown.</p>}
      {trace?.result && <div className="note info"><div className="who">Agent’s closing message</div><div className="prewrap">{trace.result}</div></div>}
      {entries.length === 0 ? <p className="empty">Nothing to show with these filters.</p>
        : <ol className="trace">{entries.map((entry, i) => entry.kind === 'tool'
            ? <ToolItem key={i} entry={entry} />
            : <PlainItem key={i} entry={entry} />)}</ol>}
      {before && <button className="btn sm" disabled={loadingOlder} onClick={loadOlder}>{loadingOlder ? 'Loading…' : 'Load earlier activity'}</button>}
      {/* Oldest last, so the note about older entries belongs at the bottom. */}
      {trace && trace.dropped > 0 && <p className="muted small">Showing the last {trace.entries.length} steps; {trace.dropped} earlier ones omitted.</p>}
      {trace && trace.entries.length === 0 && (trace.unparsed?.length ?? 0) > 0 &&
        <details><summary>Raw output ({trace.unparsed_total} lines)</summary><pre>{trace.unparsed.join('\n')}</pre></details>}
    </>}
  </Section>
}
